import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash, WhatsappLogo, CheckCircle, Warning, Clock,
  MagnifyingGlass, X, PaperPlaneTilt, Copy, Eye, EyeSlash, CalendarCheck,
  TelegramLogo, LinkSimple, EnvelopeSimple, Broadcast,
} from "@phosphor-icons/react";
import { api, brl, errMsg } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";
import Paginator from "@/components/Paginator";

const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none text-sm";

const SERVICES = [
  { v: "iptv", l: "IPTV" }, { v: "streaming", l: "Streaming" },
  { v: "vpn", l: "VPN" }, { v: "hosting", l: "Hospedagem" },
  { v: "course", l: "Curso" }, { v: "other", l: "Outro" },
];

const CHANNELS = [
  { v: "all", l: "Todos", Icon: Broadcast },
  { v: "whatsapp", l: "WhatsApp", Icon: WhatsappLogo },
  { v: "telegram", l: "Telegram", Icon: TelegramLogo },
  { v: "email", l: "Email", Icon: EnvelopeSimple },
];

const STATUS_STYLE = {
  paid: { color: "var(--success)", label: "Pago", Icon: CheckCircle },
  pending: { color: "var(--accent)", label: "Pendente", Icon: Clock },
  overdue: { color: "var(--danger)", label: "Vencido", Icon: Warning },
};

const emptyForm = {
  name: "", login: "", password: "", whatsapp: "", email: "",
  telegram_chat_id: "", preferred_channel: "all",
  plan_value: "", service_type: "iptv", server_name: "",
  payment_link: "", payment_link_type: "external", funnel_id: null,
  due_date: "", notify_before_days: 3, notify_on_day: true, notify_after_days: 1,
  custom_message: "", notes: "",
};

function SubscriberModal({ open, onClose, initial, onSaved, funnels }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...emptyForm, ...initial, plan_value: String(initial.plan_value ?? "") } : emptyForm);
      setShowPwd(false);
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const formatPhone = (v) => {
    let d = v.replace(/\D/g, "");
    if (d.startsWith("55")) d = d.slice(2);
    d = d.slice(0, 11);
    if (d.length === 0) return "";
    if (d.length <= 2) return `+55 ${d}`;
    if (d.length <= 7) return `+55 ${d.slice(0, 2)} ${d.slice(2)}`;
    return `+55 ${d.slice(0, 2)} ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const save = async () => {
    const c = form.preferred_channel;
    const needsWpp = c === "whatsapp";
    const needsEmail = c === "email";
    if (!form.name || !form.due_date || !form.plan_value) {
      return toast.error("Nome, valor e vencimento são obrigatórios");
    }
    if (needsWpp && !form.whatsapp) {
      return toast.error("WhatsApp é obrigatório quando o canal é 'Só WhatsApp'");
    }
    if (needsEmail && !form.email) {
      return toast.error("E-mail é obrigatório quando o canal é 'Só Email'");
    }
    const payload = {
      ...form,
      plan_value: parseFloat(form.plan_value),
      notify_before_days: parseInt(form.notify_before_days, 10) || 0,
      notify_after_days: parseInt(form.notify_after_days, 10) || 0,
      email: form.email || null,
      funnel_id: form.payment_link_type === "funnel" ? form.funnel_id : null,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/subscribers/${initial.id}`, payload);
      else await api.post("/subscribers", payload);
      toast.success(editing ? "Cliente atualizado" : "Cliente cadastrado");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" data-testid="subscriber-modal">
      <div className="w-full max-w-3xl my-8 rounded-sm border border-[var(--border)] bg-[var(--bg-elev)] p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="label-caps mb-1">{editing ? "Editar" : "Novo"} cliente</div>
            <h2 className="font-display font-black text-3xl tracking-tighter">
              {editing ? form.name : "Cadastrar cliente"}
            </h2>
          </div>
          <button onClick={onClose} data-testid="close-modal"
            className="text-white/40 hover:text-white p-2"><X size={20} /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="label-caps block mb-2">Nome do cliente*</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
              className={inp} data-testid="sub-name" placeholder="João Silva" />
          </div>
          <div>
            <label className="label-caps block mb-2">WhatsApp (com DDD)</label>
            <input value={form.whatsapp} onChange={(e) => set("whatsapp", formatPhone(e.target.value))}
              className={inp} data-testid="sub-whatsapp" placeholder="+55 11 99999-9999" inputMode="tel" maxLength={17} />
          </div>
          <div>
            <label className="label-caps block mb-2">E-mail</label>
            <input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)}
              className={inp} data-testid="sub-email" placeholder="cliente@email.com" />
          </div>

          <div className="md:col-span-2">
            <label className="label-caps block mb-2">Canal de notificação</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {CHANNELS.map((c) => {
                const IconC = c.Icon;
                return (
                  <button type="button" key={c.v} onClick={() => set("preferred_channel", c.v)}
                    className={`border rounded-sm py-2 flex items-center justify-center gap-2 ${
                      form.preferred_channel === c.v
                        ? "border-[var(--accent)] text-[var(--accent)]"
                        : "border-[var(--border)] text-white/60"
                    }`}
                    data-testid={`sub-channel-${c.v}`}>
                    <IconC size={14} /> {c.l}
                  </button>
                );
              })}
            </div>
          </div>

          {(form.preferred_channel === "telegram" || form.preferred_channel === "all") && (
            <div className="md:col-span-2">
              <label className="label-caps block mb-2 flex items-center gap-1">
                <TelegramLogo size={12} color="#229ED9" /> Telegram chat_id (opcional)
              </label>
              <input value={form.telegram_chat_id || ""}
                onChange={(e) => set("telegram_chat_id", e.target.value)}
                className={inp + " font-mono"} data-testid="sub-tg-chatid"
                placeholder="Preenchido automaticamente quando o cliente clicar no deep link" />
              <div className="mt-1 text-xs text-white/40">
                💡 Se você conectou seu bot em <a href="/app/telegram" className="text-[var(--accent)] underline" target="_blank" rel="noreferrer">Telegram</a>, aparecerá um botão de &quot;Enviar link&quot; na lista abaixo para o cliente iniciar o bot.
              </div>
            </div>
          )}
          <div>
            <label className="label-caps block mb-2">Login (opcional)</label>
            <input value={form.login} onChange={(e) => set("login", e.target.value)}
              className={inp} data-testid="sub-login" placeholder="usuario123" />
          </div>
          <div>
            <label className="label-caps block mb-2">Senha (opcional)</label>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} value={form.password}
                onChange={(e) => set("password", e.target.value)}
                className={inp + " pr-10"} data-testid="sub-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="label-caps block mb-2">Tipo de serviço</label>
            <select value={form.service_type} onChange={(e) => set("service_type", e.target.value)}
              className={inp} data-testid="sub-service-type">
              {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-caps block mb-2">Nome do servidor</label>
            <input value={form.server_name} onChange={(e) => set("server_name", e.target.value)}
              className={inp} data-testid="sub-server" placeholder="Ex: IPTV Premium" />
          </div>
          <div>
            <label className="label-caps block mb-2">Valor mensal (R$)*</label>
            <input type="number" step="0.01" min="0" value={form.plan_value}
              onChange={(e) => set("plan_value", e.target.value)}
              className={inp} data-testid="sub-value" placeholder="49.90" />
          </div>
          <div>
            <label className="label-caps block mb-2">Vencimento*</label>
            <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)}
              className={inp} data-testid="sub-due-date" />
          </div>

          <div className="md:col-span-2 border-t border-[var(--border)] pt-4 mt-2">
            <label className="label-caps block mb-2">Link de pagamento</label>
            <div className="flex gap-2 mb-2 text-xs">
              <button onClick={() => set("payment_link_type", "external")}
                className={`px-3 py-1 border rounded-sm ${form.payment_link_type === "external" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-white/60"}`}
                data-testid="pay-external">
                Link externo (Kirvano, Hotmart, MP, etc)
              </button>
              <button onClick={() => set("payment_link_type", "funnel")}
                className={`px-3 py-1 border rounded-sm ${form.payment_link_type === "funnel" ? "border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-white/60"}`}
                data-testid="pay-funnel">
                Funil do MercadoPagu
              </button>
            </div>
            {form.payment_link_type === "external" ? (
              <input value={form.payment_link} onChange={(e) => set("payment_link", e.target.value)}
                className={inp} data-testid="sub-pay-link" placeholder="https://..." />
            ) : (
              <select value={form.funnel_id || ""}
                onChange={(e) => {
                  const f = funnels.find((x) => x.id === e.target.value);
                  set("funnel_id", e.target.value);
                  if (f) set("payment_link", publicUrl(`/f/${f.slug}`));
                }}
                className={inp} data-testid="sub-funnel-select">
                <option value="">-- selecione um funil --</option>
                {funnels.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
            )}
          </div>

          <div className="md:col-span-2 border-t border-[var(--border)] pt-4 mt-2">
            <div className="label-caps mb-2">Notificações WhatsApp</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-white/50 block mb-1">Dias antes</label>
                <input type="number" min="0" max="30" value={form.notify_before_days}
                  onChange={(e) => set("notify_before_days", e.target.value)}
                  className={inp} data-testid="sub-notify-before" />
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">No dia</label>
                <div className="flex items-center h-[46px] gap-2">
                  <input type="checkbox" checked={form.notify_on_day}
                    onChange={(e) => set("notify_on_day", e.target.checked)}
                    className="accent-[var(--accent)] w-5 h-5" data-testid="sub-notify-onday" />
                  <span className="text-sm text-white/70">Enviar</span>
                </div>
              </div>
              <div>
                <label className="text-xs text-white/50 block mb-1">Dias depois</label>
                <input type="number" min="0" max="30" value={form.notify_after_days}
                  onChange={(e) => set("notify_after_days", e.target.value)}
                  className={inp} data-testid="sub-notify-after" />
              </div>
            </div>
            <label className="label-caps block mt-4 mb-2">Mensagem customizada (opcional)</label>
            <textarea rows={4} value={form.custom_message}
              onChange={(e) => set("custom_message", e.target.value)}
              className={inp} data-testid="sub-custom-message"
              placeholder={"Oi {name}, seu plano vence em {due_date}. Valor R$ {plan_value:.2f}. Link: {payment_link}"} />
            <div className="text-xs text-white/40 mt-1">
              Placeholders: <code className="text-[var(--accent)]">{'{name}, {due_date}, {plan_value}, {server_name}, {service_type}, {payment_link}, {login}, {password}'}</code>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="label-caps block mb-2">Observações internas</label>
            <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
              className={inp} data-testid="sub-notes" />
          </div>
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="save-subscriber"
            className="btn-primary flex items-center gap-2">
            <CheckCircle size={16} weight="fill" /> {saving ? "Salvando..." : (editing ? "Atualizar" : "Cadastrar cliente")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Clientes() {
  const [subs, setSubs] = useState([]);
  const [pg, setPg] = useState({ page: 1, pages: 1, total: 0 });
  const [stats, setStats] = useState({ paid: { count: 0, value: 0 }, pending: { count: 0, value: 0 }, overdue: { count: 0, value: 0 } });
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ open: false, initial: null });
  const [funnels, setFunnels] = useState([]);

  const load = useCallback(async (p = 1) => {
    try {
      const qs = new URLSearchParams({ page: p, per_page: 20 });
      if (filter) qs.set("status_filter", filter);
      if (search) qs.set("search", search);
      const { data } = await api.get(`/subscribers?${qs}`);
      setSubs(data.subscribers || []);
      setPg({ page: data.page, pages: data.pages, total: data.total });
      setStats(data.stats);
    } catch (e) { toast.error(errMsg(e, "Erro ao carregar clientes")); }
  }, [filter, search]);

  useEffect(() => { load(1); }, [load]);
  useEffect(() => {
    api.get("/funnels?per_page=100").then(({ data }) => setFunnels(data.funnels || [])).catch(() => {});
  }, []);

  const del = async (s) => {
    if (!window.confirm(`Excluir cliente "${s.name}"?`)) return;
    await api.delete(`/subscribers/${s.id}`);
    toast.success("Cliente removido");
    load(pg.page);
  };

  const sendTgDeepLink = async (s) => {
    const link = s.telegram_deep_link;
    if (!link) {
      return toast.error("Conecte seu bot em /app/telegram primeiro");
    }
    const msg = `Olá ${s.name}! Para receber notificações do seu plano ${s.service_type?.toUpperCase() || ""} por Telegram, clique aqui: ${link}`;
    // Se WhatsApp está configurado, oferece envio pelo whatsapp da plataforma
    try {
      const { data } = await api.post(`/subscribers/${s.id}/notify`, { message: msg });
      if (data.all_mock) {
        // Não conseguiu enviar por nenhum canal — copia para clipboard
        await navigator.clipboard.writeText(link);
        toast.info(`Link copiado: ${link}`);
      } else {
        toast.success(`Link enviado via ${data.sent_via}!`);
      }
    } catch (e) {
      await navigator.clipboard.writeText(link);
      toast.info("Link copiado para você compartilhar manualmente");
    }
  };

  const notifyNow = async (s) => {
    try {
      const { data } = await api.post(`/subscribers/${s.id}/notify`, {});
      if (data.all_mock) {
        const channels = data.channels || {};
        const errs = [];
        if (channels.whatsapp?.wpp_error) errs.push(`WhatsApp: ${channels.whatsapp.wpp_error}`);
        if (channels.whatsapp?.mock && !channels.whatsapp.wpp_error) errs.push("WhatsApp desconectado — escaneie o QR em /app/whatsapp");
        if (channels.telegram?.error) errs.push(`Telegram: ${channels.telegram.error}`);
        if (channels.email?.error) errs.push(`Email: ${channels.email.error}`);
        if (channels._none?.error) errs.push(channels._none.error);
        toast.error(errs.join(" · ") || "Nenhum canal enviou a mensagem");
      } else {
        toast.success(`Mensagem enviada via ${data.sent_via}!`);
      }
    } catch (e) { toast.error(errMsg(e, "Erro ao enviar")); }
  };

  const markPaid = async (s) => {
    if (!window.confirm(`Marcar ${s.name} como pago e adiantar vencimento 30 dias?`)) return;
    const { data } = await api.post(`/subscribers/${s.id}/mark-paid`);
    toast.success(`Renovado. Próximo vencimento: ${new Date(data.new_due_date).toLocaleDateString("pt-BR")}`);
    load(pg.page);
  };

  return (
    <div className="fadeUp h-[calc(100vh-4rem)] md:h-[calc(100vh-6rem)] flex flex-col gap-4">
      {/* HEADER compacto — 1 linha */}
      <header className="flex items-center justify-between shrink-0" data-testid="clientes-header">
        <div>
          <div className="label-caps text-[10px]">CRM · Assinaturas recorrentes</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-none mt-1">Clientes</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-[280px] hidden md:block">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-subscribers"
              placeholder="Buscar nome, e-mail, WhatsApp..."
              className={inp + " pl-9 py-2 text-xs"} />
          </div>
          <button onClick={() => setModal({ open: true, initial: null })} data-testid="new-subscriber-btn"
            className="btn-primary flex items-center gap-2 py-2 px-4 text-sm">
            <Plus size={16} weight="bold" /> Novo cliente
          </button>
        </div>
      </header>

      {/* KPIs compactos — 3 chips horizontais */}
      <div className="grid grid-cols-3 gap-3 shrink-0" data-testid="clientes-kpis">
        {["pending", "paid", "overdue"].map((k) => {
          const s = STATUS_STYLE[k];
          const stat = stats[k] || { count: 0, value: 0 };
          const active = filter === k;
          return (
            <button key={k} onClick={() => setFilter(filter === k ? "" : k)}
              data-testid={`stat-${k}`}
              className={`card-flat px-4 py-3 text-left transition flex items-center gap-3 ${active ? "border-[var(--accent)] ring-1 ring-[var(--accent)]/40" : "hover:border-white/20"}`}>
              <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: `${s.color}18`, color: s.color }}>
                <s.Icon size={18} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-xl leading-none" style={{ color: s.color }}>{stat.count}</span>
                  <span className="label-caps text-[10px]">{s.label}</span>
                </div>
                <div className="text-[11px] text-white/50 mt-1 truncate">{brl(stat.value)}/mês recorrente</div>
              </div>
              {active && <X size={12} className="text-white/40" />}
            </button>
          );
        })}
      </div>

      {/* Busca em mobile */}
      <div className="md:hidden shrink-0">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..." className={inp + " pl-9 py-2 text-xs"} />
        </div>
      </div>

      {/* LISTA — flex-1, scroll interno */}
      <div className="flex-1 min-h-0 card-flat overflow-hidden flex flex-col" data-testid="clientes-list">
        <div className="grid grid-cols-[1fr_140px_160px_180px] gap-3 px-5 py-2.5 border-b border-[var(--border)] label-caps text-[10px] bg-white/[0.02] shrink-0">
          <div>Cliente / Serviço</div>
          <div>Valor</div>
          <div>Vencimento</div>
          <div className="text-right">Ações</div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-[var(--border)]">
          {subs.length === 0 ? (
            <div className="p-10 text-center h-full flex flex-col items-center justify-center" data-testid="no-subscribers">
              <div className="text-white/50 mb-4 text-sm">
                {search || filter ? "Nenhum cliente com esses filtros." : "Você ainda não cadastrou clientes."}
              </div>
              {!search && !filter && (
                <button onClick={() => setModal({ open: true, initial: null })}
                  className="btn-primary inline-flex items-center gap-2 text-sm">
                  <Plus size={14} /> Cadastrar primeiro cliente
                </button>
              )}
            </div>
          ) : subs.map((s) => {
            const st = STATUS_STYLE[s.payment_status] || STATUS_STYLE.pending;
            const daysUntil = s.days_until_due;
            return (
              <div key={s.id} className="grid grid-cols-[1fr_140px_160px_180px] gap-3 px-5 py-3 hover:bg-white/[0.03] transition items-center" data-testid={`sub-row-${s.id}`}>
                {/* Coluna Cliente */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
                    style={{ borderColor: st.color, color: st.color }}>
                    <st.Icon size={16} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-sm truncate">{s.name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm border font-mono uppercase leading-none"
                        style={{ borderColor: st.color, color: st.color }}>{st.label}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-white/60 uppercase leading-none">
                        {SERVICES.find((x) => x.v === s.service_type)?.l}
                      </span>
                    </div>
                    <div className="mt-1 flex gap-3 text-[11px] text-white/50 truncate">
                      <span className="flex items-center gap-1 truncate"><WhatsappLogo size={11} /> {s.whatsapp || "—"}</span>
                      {s.server_name && <span className="truncate">{s.server_name}</span>}
                    </div>
                  </div>
                </div>

                {/* Coluna Valor */}
                <div>
                  <div className="font-display font-bold text-sm">{brl(s.plan_value)}</div>
                  <div className="text-[10px] text-white/40">/mês</div>
                </div>

                {/* Coluna Vencimento */}
                <div>
                  <div className="font-semibold text-sm">{new Date(s.due_date).toLocaleDateString("pt-BR")}</div>
                  {typeof daysUntil === "number" && (
                    <div className="text-[10px]" style={{ color: daysUntil < 0 ? "var(--danger)" : daysUntil <= 3 ? "var(--accent)" : "rgba(255,255,255,0.4)" }}>
                      {daysUntil === 0 ? "hoje" : daysUntil > 0 ? `em ${daysUntil}d` : `${Math.abs(daysUntil)}d atrás`}
                    </div>
                  )}
                </div>

                {/* Coluna Ações */}
                <div className="flex gap-1 justify-end">
                  {s.telegram_deep_link && !s.telegram_connected && (
                    <button onClick={() => sendTgDeepLink(s)} data-testid={`tg-link-${s.id}`}
                      title="Enviar link Telegram" className="btn-ghost text-xs !p-1.5" style={{ color: "#229ED9" }}>
                      <LinkSimple size={13} weight="bold" />
                    </button>
                  )}
                  {s.telegram_connected && (
                    <span title="Telegram conectado" className="btn-ghost text-xs !p-1.5 opacity-70" style={{ color: "#229ED9" }}>
                      <TelegramLogo size={13} weight="fill" />
                    </span>
                  )}
                  <button onClick={() => notifyNow(s)} data-testid={`notify-${s.id}`} title="Enviar cobrança agora"
                    className="btn-ghost text-xs !p-1.5 text-[var(--success)]"><PaperPlaneTilt size={13} weight="fill" /></button>
                  <button onClick={() => markPaid(s)} data-testid={`markpaid-${s.id}`} title="Marcar como pago (+30d)"
                    className="btn-ghost text-xs !p-1.5 text-[var(--accent)]"><CalendarCheck size={13} weight="fill" /></button>
                  <button onClick={() => setModal({ open: true, initial: s })} data-testid={`edit-${s.id}`}
                    title="Editar" className="btn-ghost text-xs !p-1.5"><Pencil size={13} /></button>
                  <button onClick={() => del(s)} data-testid={`del-${s.id}`}
                    title="Excluir" className="btn-ghost text-xs !p-1.5 text-[var(--danger)]"><Trash size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
        {pg.pages > 1 && (
          <div className="px-5 py-2 border-t border-[var(--border)] shrink-0">
            <Paginator page={pg.page} pages={pg.pages} onChange={load} total={pg.total} />
          </div>
        )}
      </div>

      <SubscriberModal
        open={modal.open}
        initial={modal.initial}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={() => load(pg.page)}
        funnels={funnels}
      />
    </div>
  );
}
